/**
 * Telling a crew about the day they are booked.
 *
 * Until now a scheduled work order reached nobody outside the office. The Cal
 * tab showed it, and crews are not users of Roof Board — they have no login —
 * so the day was passed on by somebody remembering to pass it on. Jonathon,
 * 2026-09-01: "is it going to show up on crews calendar for their work order
 * scheduled? that needs to happen."
 *
 * Two ways out, because crews are not one kind of contact. Some have an email
 * address and keep a calendar; plenty are a name and a phone number. So the
 * booking goes to a Google Calendar with the crew invited *and* as a text, and
 * neither is assumed to have worked.
 *
 * Everything here is a pure function of the work order. Nothing talks to Google
 * or Twilio — `calendar-sync` does the first, `twilio-send` the second. What
 * lives here is the part worth being sure about: which day the crew is told
 * about, what the event says, what the text says, and when there is nothing new
 * to tell them.
 */

import { jobAddress } from "./search.js";
import { COMPANY_NAME } from "./constants.js";
import type { CalendarEvent } from "./calendarEvent.js";
import type { Job } from "./types.js";

/**
 * How long a crew's day is assumed to run when nobody has said.
 *
 * A work order carries `start_at` and `end_at`, and `end_at` is filled in about
 * as often as anybody fills in an optional field. Eight hours is a working day;
 * the point of the number is that the event occupies the day rather than
 * looking like a fifteen-minute call.
 */
export const DEFAULT_WORK_MINUTES = 8 * 60;

/**
 * The trades on a work order, as one phrase.
 *
 * "Roofing Replacement & Gutters" rather than a bare array printed by accident,
 * which is what a template literal does to one.
 */
export function tradesPhrase(
  trades: string | readonly string[] | null | undefined,
): string {
  const list = Array.isArray(trades)
    ? trades.map((t) => String(t).trim()).filter(Boolean)
    : [String(trades ?? "").trim()].filter(Boolean);
  return list.join(" & ");
}

/** The shape of a work order this module needs. Deliberately narrow. */
export interface CrewBookingOrder {
  order_number?: unknown;
  /**
   * What the crew is there to do. Text on the older rows and a text[] since
   * migration 20260731160000, because one visit can cover two trades — so both
   * shapes have to read the same on a phone.
   */
  trades?: string | readonly string[] | null;
  description?: string | null;
  office_notes?: string | null;
  /** The day the crew actually works. See `bookedDay`. */
  start_at?: string | null;
  end_at?: string | null;
  /** The checklist tick — *when it was scheduled*, not what it is scheduled for. */
  scheduled_at?: string | null;
  /** The `start_at` the crew was last told about, if they have been told. */
  crew_told_for?: string | null;
}

export interface CrewContact {
  name?: string | null;
  email?: string | null;
  cell?: string | null;
  phone?: string | null;
}

/**
 * The day a crew is booked for.
 *
 * `scheduled_at` is not it, and the difference has been on screen since the Cal
 * tab was built. `scheduled_at` is a checklist stamp — the moment somebody
 * pressed "Schedule" — so a job ticked on Monday for the following Tuesday has
 * a `scheduled_at` of Monday. `start_at` is the box on the work order that asks
 * when the crew starts, and it is the only field that answers the question.
 *
 * Returns null when the order is scheduled but nobody has said which day, which
 * is a real state and not an error: it means the office knows it is going ahead
 * and has not booked the day yet. Nothing is sent, and nothing is put on a
 * calendar, because a booking with no day is not a booking.
 */
export function bookedDay(order: CrewBookingOrder): string | null {
  const start = (order.start_at ?? "").trim();
  if (start === "") return null;
  const at = new Date(start);
  return Number.isNaN(at.getTime()) ? null : start;
}

/** Whether this work order is one a crew should be hearing about at all. */
export function isCrewBooked(order: CrewBookingOrder): boolean {
  return Boolean(order.scheduled_at) && bookedDay(order) !== null;
}

/**
 * Whether there is something new to tell the crew.
 *
 * Told once, for a given day, and not again — a crew re-texted every time
 * somebody saves the work order stops reading the texts, which costs more than
 * the one that mattered. What restarts it is the day changing: a move from
 * Tuesday to Thursday is the message this whole feature exists to deliver.
 *
 * Unscheduling counts as news too, and is handled by the caller rather than
 * here: this answers "is the booking different from what they were told", and a
 * booking that has gone away is a different question with a different message.
 */
export function crewNeedsTelling(order: CrewBookingOrder): boolean {
  if (!isCrewBooked(order)) return false;
  const told = (order.crew_told_for ?? "").trim();
  if (told === "") return true;
  // Compared as instants, not strings. The same moment reaches us written two
  // ways — with a Z from Postgres, with an offset from a browser — and a string
  // comparison would re-text a crew because a timezone was spelled differently.
  const a = new Date(told).getTime();
  const b = new Date(bookedDay(order)!).getTime();
  return !(Number.isFinite(a) && Number.isFinite(b) && a === b);
}

/** When the crew's day ends. A missing or broken end must not precede the start. */
export function bookingEnd(startIso: string, endIso?: string | null): string {
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : null;
  if (end && !Number.isNaN(end.getTime()) && end.getTime() > start.getTime())
    return end.toISOString();
  return new Date(
    start.getTime() + DEFAULT_WORK_MINUTES * 60_000,
  ).toISOString();
}

/**
 * The event the crew is invited to.
 *
 * Same rule as an inspection's, for the same reason: no price. A crew calendar
 * entry is forwarded and screenshotted, and what a job is worth is not
 * something to put on one. What it does carry is what somebody pulling up to
 * the house needs — the trade, the address, the office's note and the job
 * number to quote on the phone.
 */
export function crewBookingEvent(input: {
  order: CrewBookingOrder;
  job: Job;
  crewName?: string | null;
  invitees: readonly string[];
}): CalendarEvent | null {
  const start = bookedDay(input.order);
  // No day, no event. The caller uses this null to know to delete rather than
  // update — unscheduling is how a booking comes off a calendar.
  if (start === null) return null;

  const { order, job } = input;
  const contact = job.contact ?? {};
  const customer = contact.full_name ?? "";
  const phone = contact.cell ?? contact.phone ?? "";
  const trades = tradesPhrase(order.trades) || "Work";

  const description = [
    order.description ?? "",
    order.office_notes ?? "",
    "",
    input.crewName ? `Crew: ${input.crewName}` : "",
    customer ? `Customer: ${customer}` : "",
    phone ? `Phone: ${phone}` : "",
    `Job #: ${job.job_number}`,
    order.order_number ? `Work order #: ${order.order_number}` : "",
  ]
    .filter((line, index) => line !== "" || index === 2)
    .join("\n")
    .trim();

  return {
    summary: `${trades} — ${input.crewName || customer || `Job #${job.job_number}`}`,
    description,
    location: jobAddress(job),
    start: { dateTime: start },
    end: { dateTime: bookingEnd(start, order.end_at) },
    attendees: input.invitees.map((email) => ({ email })),
  };
}

/**
 * The number to text a crew on.
 *
 * A crew row carries one `phone`, which in practice is a mobile — these are
 * roofers, not offices. `cell` is read first anyway because contacts have both
 * and a crew may grow one; what matters is that neither being present returns
 * null rather than an empty string, so nothing can send a message to "".
 */
export function crewTextNumber(
  crew: CrewContact | null | undefined,
): string | null {
  const number = (crew?.cell ?? "").trim() || (crew?.phone ?? "").trim();
  return number === "" ? null : number;
}

/**
 * What the crew is told, in a text.
 *
 * Written to be read on a phone in one glance and to survive being read a week
 * later out of context, which is why it names the company and the job number.
 * No price, for the reason the event carries none.
 *
 * The date is formatted by the caller's locale on purpose — this runs in the
 * browser, on the phone of somebody in the same timezone as the roof. Passing a
 * formatted string in would have meant a second place that decides what a date
 * looks like.
 */
export function crewBookingText(input: {
  order: CrewBookingOrder;
  job: Job;
  companyName?: string | null;
  /** The booked day, already written the way a person reads it. */
  when: string;
}): string | null {
  if (bookedDay(input.order) === null) return null;
  const company = (input.companyName ?? "").trim() || COMPANY_NAME;
  const trades = tradesPhrase(input.order.trades) || "Work";
  const where = jobAddress(input.job);

  return [
    `${company}: you are booked for ${input.when}.`,
    `${trades} — ${where || `Job #${input.job.job_number}`}.`,
    `Job #${input.job.job_number}.`,
  ].join(" ");
}

/** What the crew is told when the day is taken back off them. */
export function crewCancelledText(input: {
  job: Pick<Job, "job_number">;
  companyName?: string | null;
  /** The day they had been told about. */
  when: string;
}): string {
  const company = (input.companyName ?? "").trim() || COMPANY_NAME;
  return `${company}: job #${input.job.job_number} on ${input.when} is off the schedule. We will be in touch with a new day.`;
}

/** A work order's lines, as the crew reads them. */
export interface CrewWorkOrderLine {
  description?: string | null;
  qty?: unknown;
  unit?: unknown;
}

export interface CrewWorkOrderEmail {
  subject: string;
  body: string;
}

/**
 * The work order, as an email to the crew.
 *
 * Lysa, 2026-09-12: "When scheduling WOs and MOs I can't tell if anything was
 * sent to the crews or distributor. I'd like either a pop up like the one when
 * I send an estimate where I can actually press 'send WO/send MO'… that way I
 * can add a note in the email if i need to."
 *
 * The calendar invitation and the text already say *when* (ADR 0117). Neither
 * says *what*, and neither leaves anything the crew can open on site, so the
 * scope went out however somebody chose to send it and the office had no record
 * that it had. This is the covering letter for the work order itself, which
 * goes as a PDF.
 *
 * **No money in the letter.** The same rule the invitation and the text follow:
 * a crew email is forwarded and screenshotted. What the crew is paid is on the
 * attached PDF, which is a document rather than a message, and only when
 * whoever sends it leaves the pay ticked.
 *
 * The letter names the homeowner and the address because that is what the crew
 * is looking for when they read it back on the driveway — Lysa asked for the
 * same two facts on the PDF, for the same reason.
 */
export function crewWorkOrderEmail(input: {
  order: CrewBookingOrder;
  job: Job;
  crewName?: string | null;
  companyName?: string | null;
  /** The booked day, already written the way a person reads it. Null if unbooked. */
  when?: string | null;
  lines?: readonly CrewWorkOrderLine[];
  /** The PDF hanging off it, named so the letter points at it. */
  attachmentName?: string | null;
}): CrewWorkOrderEmail {
  const { order, job, crewName, when, lines = [], attachmentName } = input;
  const company = (input.companyName ?? "").trim() || COMPANY_NAME;
  const where = jobAddress(job);
  const number = order.order_number ? `WO-${order.order_number}` : "Work order";
  const customer = job.contact?.full_name ?? "";
  const trades = tradesPhrase(order.trades) || "Work";

  const scope = lines
    .map((line) => {
      const what = (line.description ?? "").trim();
      const qty = `${line.qty ?? 0} ${String(line.unit ?? "").trim()}`.trim();
      return `  ${qty}  ${what}`.trimEnd();
    })
    .filter((line) => line.trim() !== "");

  const body = [
    crewName ? `${crewName},` : "",
    "",
    `Here is the work order for ${where || `job #${job.job_number}`}.`,
    "",
    `Work order: ${number}`,
    customer ? `Homeowner: ${customer}` : "",
    where ? `Address: ${where}` : "",
    when ? `Day: ${when}` : "Day: to be confirmed — we will let you know.",
    `Work: ${trades}`,
    order.description ? `Description: ${order.description}` : "",
    "",
    scope.length > 0 ? "Scope:" : "",
    ...scope,
    "",
    attachmentName
      ? `Attached: the work order as a PDF (${attachmentName}).`
      : "",
    `Please read it before you start, and call the office with anything that does not match the roof.`,
    "",
    `Thank you,`,
    company,
  ]
    .filter(
      (line, at, all) => !(line === "" && (at === 0 || all[at - 1] === "")),
    )
    .join("\n")
    .trim();

  return {
    subject: `${number}${where ? ` — ${where}` : ""}${
      customer ? ` (${customer})` : ""
    }`,
    body: `${body}\n`,
  };
}
